import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Props for QuickActionCard component
 */
export interface QuickActionCardProps {
  /**
   * Action title
   */
  title: string;

  /**
   * Action description
   */
  description?: string;

  /**
   * Icon to display
   */
  icon: React.ReactNode;

  /**
   * Link href (for navigation)
   */
  href?: string;

  /**
   * Click handler (for actions)
   */
  onClick?: () => void;

  /**
   * Optional status badge
   */
  status?: {
    label: string;
    variant: 'success' | 'warning' | 'info' | 'default';
  };

  /**
   * Color variant for the card
   */
  variant?: 'primary' | 'success' | 'warning' | 'info' | 'default';

  /**
   * Whether the action is disabled
   */
  disabled?: boolean;

  /**
   * Custom className
   */
  className?: string;
}

/**
 * Get variant styles
 */
const getVariantStyles = (variant: QuickActionCardProps['variant'] = 'default') => {
  const styles = {
    primary: {
      gradient: 'from-primary-500/10 to-blue-500/10',
      border: 'border-primary-200',
      icon: 'bg-gradient-to-br from-primary-500 to-primary-700 text-white',
      hover: 'hover:border-primary-300 hover:shadow-primary-200/50',
    },
    success: {
      gradient: 'from-green-500/10 to-emerald-500/10',
      border: 'border-green-200',
      icon: 'bg-gradient-to-br from-green-500 to-emerald-600 text-white',
      hover: 'hover:border-green-300 hover:shadow-green-200/50',
    },
    warning: {
      gradient: 'from-amber-500/10 to-orange-500/10',
      border: 'border-amber-200',
      icon: 'bg-gradient-to-br from-amber-500 to-orange-600 text-white',
      hover: 'hover:border-amber-300 hover:shadow-amber-200/50',
    },
    info: {
      gradient: 'from-blue-500/10 to-cyan-500/10',
      border: 'border-blue-200',
      icon: 'bg-gradient-to-br from-blue-500 to-cyan-600 text-white',
      hover: 'hover:border-blue-300 hover:shadow-blue-200/50',
    },
    default: {
      gradient: 'from-gray-50 to-gray-100/50',
      border: 'border-gray-200',
      icon: 'bg-gradient-to-br from-gray-600 to-gray-700 text-white',
      hover: 'hover:border-gray-300 hover:shadow-gray-200/50',
    },
  };
  return styles[variant];
};

/**
 * Get status badge styles
 */
const getStatusStyles = (variant: 'success' | 'warning' | 'info' | 'default') => {
  const styles = {
    success: 'bg-green-100 text-green-700 ring-1 ring-green-600/20',
    warning: 'bg-amber-100 text-amber-700 ring-1 ring-amber-600/20',
    info: 'bg-blue-100 text-blue-700 ring-1 ring-blue-600/20',
    default: 'bg-gray-100 text-gray-700 ring-1 ring-gray-600/20',
  };
  return styles[variant];
};

/**
 * QuickActionCard Component
 *
 * A clickable card for common dashboard actions.
 * Provides quick access to frequently used features with visual feedback.
 *
 * @example
 * ```tsx
 * <QuickActionCard
 *   title="Create Campaign"
 *   description="Set up a new filtering campaign"
 *   icon={<PlusIcon />}
 *   href="/campaigns/create"
 *   variant="primary"
 *   status={{ label: "Quick Start", variant: "info" }}
 * />
 * ```
 */
export function QuickActionCard({
  title,
  description,
  icon,
  href,
  onClick,
  status,
  variant = 'default',
  disabled = false,
  className = '',
}: QuickActionCardProps) {
  const styles = getVariantStyles(variant);

  const baseClasses = `
    group relative overflow-hidden
    bg-gradient-to-br ${styles.gradient}
    border-2 ${styles.border}
    rounded-xl p-6
    transition-all duration-300
    ${disabled ? 'opacity-60 cursor-not-allowed' : `cursor-pointer ${styles.hover} hover:shadow-lg hover:scale-105`}
    ${className}
  `;

  const content = (
    <>
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-24 h-24 bg-white/30 rounded-full blur-2xl -mr-12 -mt-12 group-hover:scale-150 transition-transform duration-500"></div>

      {/* Status Badge */}
      {status && (
        <div className="absolute top-4 right-4" data-testid="action-status">
          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${getStatusStyles(status.variant)}`}>
            {status.label}
          </span>
        </div>
      )}

      {/* Icon */}
      <div
        className={`w-14 h-14 rounded-xl ${styles.icon} shadow-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}
        data-testid="action-icon"
      >
        <div className="w-7 h-7">{icon}</div>
      </div>

      {/* Content */}
      <div className="relative z-10">
        <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-gray-700 transition-colors" data-testid="action-title">
          {title}
        </h3>
        {/* Arrow Indicator */}
        <div className="flex items-center text-sm font-semibold text-gray-500 group-hover:text-gray-700 transition-colors">
          <span className="mr-2">Get started</span>
          <svg
            className="w-4 h-4 transform group-hover:translate-x-1 transition-transform"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </div>
    </>
  );

  if (disabled) {
    return (
      <div className={baseClasses} data-testid="quick-action-card-disabled">
        {content}
      </div>
    );
  }

  if (href) {
    return (
      <Link to={href} className={baseClasses} data-testid="quick-action-card">
        {content}
      </Link>
    );
  }

  return (
    <button onClick={onClick} className={baseClasses} data-testid="quick-action-card">
      {content}
    </button>
  );
}

export default QuickActionCard;
